import React from 'react';

import CleanBanner from '@/components/corporate/CleanBanner';

const RED = '#b8313d';

const visionIcons: Record<string, React.ReactNode> = {
  Needs: (
    <svg viewBox="0 0 40 40" width={44} height={44} aria-hidden>
      <circle cx={20} cy={20} r={15} fill="none" stroke={RED} strokeWidth={3} />
      <circle cx={20} cy={20} r={5} fill={RED} />
    </svg>
  ),
  Technology: (
    <svg viewBox="0 0 40 40" width={44} height={44} aria-hidden>
      <polygon
        points="20,4 33,11.5 33,28.5 20,36 7,28.5 7,11.5"
        fill="none"
        stroke={RED}
        strokeWidth={3}
        strokeLinejoin="round"
      />
      <circle cx={20} cy={20} r={4} fill={RED} />
    </svg>
  ),
  Value: (
    <svg viewBox="0 0 40 40" width={44} height={44} aria-hidden>
      <polygon points="20,5 33,20 25,20 25,35 15,35 15,20 7,20" fill={RED} />
    </svg>
  ),
};

const pillars = [
  { key: 'Needs', title: 'Needs', desc: '고객과 사회가 필요로 하는 가치를 먼저 읽습니다.' },
  { key: 'Technology', title: 'Technology', desc: '데이터·AI 핵심 기술로 문제를 해결합니다.' },
  { key: 'Value', title: 'Value', desc: '기술을 실질적인 가치로 전환합니다.' },
];

const bizChips = ['API 구축', '시스템 운영·유지보수', '공공데이터 개방', 'AI'];

const expertise = [
  {
    title: '개방DB 및 Open API 구축 전문성',
    items: [
      '금융위원회·외교부·한국조폐공사 등 다수의 국가중점데이터 개방DB 및 Open API 구축',
      '오픈소스 기반 Open API 시스템(Data Infuser) 개발기업으로 18개 공공기관에 적용 중',
      '공적마스크 판매현황·요소수 재고 API 등 긴급 정보개방 서비스 다수 수행',
      '공공·민간 10개 분야 플랫폼과 113개 센터 간 연계 기술',
    ],
  },
  {
    title: '공공데이터포털 구축·운영 기술',
    items: [
      '공공데이터포털 다년 연속 운영·구축 사업자',
      '5천여 개 기관과의 데이터 등록·시스템 연계 운영',
      '멀티 클라우드 기반 연계 정책 운영',
      '공공빅데이터 저장·관리·조회, Open API 연계, 시각화 구축·운영',
      '공공데이터 등록 규격 마련 및 지침서 제공',
    ],
  },
  {
    title: '민간·공공 클라우드 기반 시스템 구축 기술',
    items: [
      '하이브리드(민간+공공) 클라우드 기반 시스템 구축 전문성',
      '클라우드 네이티브 기술 전문성',
      '민간 클라우드 기반 데이터 체계·Open API 통합 서비스 구축',
    ],
  },
  {
    title: 'AI·LLM 기반 지능형 서비스',
    items: [
      '정부 최초 MCP(Model Context Protocol) 서버 구축 및 공공서비스 연계 PoC 수행',
      'LLM 기반 AI Agent 시스템 개발 및 전문성 확장',
      'RAG·GraphRAG 기반 검색·질의응답 기술 보유',
      'AI Data Verifier 등 데이터 검증·품질 분야에 AI 적용',
    ],
  },
];

const orgUnits = [
  { name: 'R&D 센터', sub: '산학 연계' },
  { name: '개발본부', sub: '플랫폼·API' },
  { name: '사업운영본부', sub: '구축·운영' },
  { name: '경영지원', sub: '경영·관리' },
];

const Company: React.FC = () => {
  return (
    <div className="cp">
      <CleanBanner eyebrow="ABOUT US" title="회사소개" desc="기업 개요 및 조직도" />

      {/* 비전 */}
      <section className="cp-section">
        <div className="cp__container">
          <div className="cp-head" style={{ maxWidth: 880, margin: '0 auto 56px' }}>
            <div className="cp-head__eyebrow">VISION</div>
            <h2 className="cp-head__title" style={{ lineHeight: 1.3 }}>
              차별화된 <span className="cp-emph">Needs · Technology · Value</span>의<br />
              조화를 꿈꾸는 기업입니다.
            </h2>
          </div>
          <div className="cp-vision">
            {pillars.map((p) => (
              <div key={p.key} className="cp-vision__card">
                <div className="cp-vision__icon">{visionIcons[p.key]}</div>
                <div className="cp-vision__title">{p.title}</div>
                <div className="cp-vision__desc">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 주요사업 */}
      <section className="cp-section cp-section--soft">
        <div className="cp__container" style={{ textAlign: 'center' }}>
          <div className="cp-head__eyebrow">CORE BUSINESS</div>
          <h2 className="cp-head__title" style={{ marginBottom: 20 }}>
            공공 SI 분야의 전문기업
          </h2>
          <p
            style={{
              margin: '0 auto 30px',
              maxWidth: 680,
              fontSize: 17,
              lineHeight: 1.7,
              color: '#565b70',
            }}
          >
            프람트테크놀로지는 API 구축, 시스템 운영·유지보수 등{' '}
            <b style={{ color: '#181b24', fontWeight: 700 }}>공공 SI 분야의 전문기업</b>입니다.
          </p>
          <div className="cp-chips">
            {bizChips.map((b) => (
              <span key={b} className="cp-chips__chip">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 사업분야 */}
      <section className="cp-section">
        <div className="cp__container">
          <div className="cp-head">
            <div className="cp-head__eyebrow">EXPERTISE</div>
            <h2 className="cp-head__title">사업분야</h2>
            <p className="cp-head__desc">
              창립 이래 데이터 분야의 다양한 경험과 기술을 확보한 빅데이터 전문기업입니다.
            </p>
          </div>
          <div className="cp-expertise">
            {expertise.map((e) => (
              <div key={e.title} className="cp-expertise__card">
                <div className="cp-expertise__head">{e.title}</div>
                <div className="cp-expertise__body">
                  {e.items.map((it) => (
                    <div key={it} className="cp-expertise__item">
                      <span className="cp-dia" />
                      <div>{it}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 조직 및 인원 */}
      <section className="cp-section cp-section--soft-top">
        <div className="cp__container">
          <div className="cp-head">
            <div className="cp-head__eyebrow">ORGANIZATION</div>
            <h2 className="cp-head__title">조직 및 인원</h2>
            <p className="cp-head__desc">
              산학 연계 조직 구성으로 R&amp;D 분야의 차별화된 전문성과 인력을 확보하고 있습니다.
            </p>
          </div>
          <div className="cp-org">
            <div className="cp-org__top">
              <div className="cp-org__ceo">대표이사</div>
            </div>
            <div className="cp-org__stem" />
            <div className="cp-org__branch">
              <div className="cp-org__bus" />
              <div className="cp-org__units">
                {orgUnits.map((u) => (
                  <div key={u.name} className="cp-org__unit">
                    <div className="cp-org__drop" />
                    <div className="cp-org__box">
                      <div className="cp-org__name">{u.name}</div>
                      <div className="cp-org__sub">{u.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="cp-org__note">※ 조직도는 예시이며, 실제 조직 구성으로 대체됩니다.</div>
          </div>
        </div>
      </section>

      {/* 마무리 */}
      <section className="cp-section">
        <div className="cp__container">
          <div className="cp-closing">
            <div className="cp-closing__blob" aria-hidden />
            <h2>
              프람트테크놀로지는 공공분야 SI 전문기업으로서
              <br />
              새로운 도전과 혁신으로 시장을 주도해 나갑니다.
            </h2>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Company;
